// Client for paygate's tenant provider-config API (via the gateway,
// /api/v1/paygate/*). Lets a company admin manage their own payout provider.
// Credentials are WRITE-ONLY: reads never return secrets, only `hasCredentials`.
import axios from 'axios';
import { apiConfig } from '@/config/api.config';
import { attachCookieAuth } from '@/lib/session';
import type { Currency } from '@/features/payroll/types';

const BASE = '/api/v1/paygate';

const http = axios.create({ baseURL: apiConfig.baseURL, timeout: 20000, withCredentials: true });
http.interceptors.request.use(attachCookieAuth);

export type ProviderCode = 'bankfile' | 'azampay' | 'nmb' | 'crdb' | 'mpesa' | 'selcom' | 'sftp' | 'mock';

/** One entry in a batch's SFTP transmission trail. */
export interface TransmissionEntry {
  state: string; // uploaded | bank_received | bank_accepted | rejected | reconciliation_required | …
  provider_ref?: string;
  remote_path?: string;
  detail?: string;
  occurred_at: string;
}
export type PayoutChannel = '' | 'MOBILE_MONEY' | 'BANK';
export type ProviderEnv = 'sandbox' | 'live';

export interface ProviderConfig {
  /** Connection id (one per company × provider × environment). */
  id: string;
  /** Paying company (legal entity); undefined = the workspace-level connection. */
  legalEntityId?: string;
  providerCode: ProviderCode;
  channel: PayoutChannel;
  enabled: boolean;
  environment: ProviderEnv;
  priority: number;
  hasCredentials: boolean;
  /** Increments on every credential save (rotation); 0 = never saved. */
  credentialVersion: number;
  disabledAt?: string;
  lastTestAt?: string;
  lastTestOk?: boolean;
  lastTestDetail?: string;
  updatedAt: string;
}

/** The connections listing plus what the caller may do with it. */
export interface ConnectionsView {
  connections: ProviderConfig[];
  /** paygate:admin — may create / rotate / test / disable. */
  canManage: boolean;
  /** paygate:admin + payment release (owner) — may choose Production. */
  canChooseLive: boolean;
  /** Production connections exist only on the production service. */
  liveAvailable: boolean;
}

export interface ConnectionTestResult {
  ok: boolean;
  detail: string;
}

export interface SaveProviderInput {
  /** Paying company (legal entity) the connection belongs to; omit for workspace-level. */
  legalEntityId?: string;
  channel?: PayoutChannel;
  enabled?: boolean;
  environment?: ProviderEnv;
  priority?: number;
  /** Non-secret config (stored as-is). */
  config?: Record<string, unknown>;
  /** Plaintext secrets — sealed server-side, never stored or returned raw.
   *  Omit (or null) to leave existing credentials untouched. */
  credentials?: Record<string, unknown> | null;
}

function mapConfig(c: any): ProviderConfig {
  return {
    id: c.id ?? '',
    legalEntityId: c.legal_entity_id || undefined,
    providerCode: c.provider_code,
    channel: (c.channel ?? '') as PayoutChannel,
    enabled: !!c.enabled,
    environment: (c.environment ?? 'sandbox') as ProviderEnv,
    priority: Number(c.priority ?? 100),
    hasCredentials: !!c.has_credentials,
    credentialVersion: Number(c.credential_version ?? 0),
    disabledAt: c.disabled_at || undefined,
    lastTestAt: c.last_test_at || undefined,
    lastTestOk: typeof c.last_test_ok === 'boolean' ? c.last_test_ok : undefined,
    lastTestDetail: c.last_test_detail || undefined,
    updatedAt: c.updated_at ?? '',
  };
}

// --- payout ledger (read-only) ---

export interface PayoutBatch {
  id: string;
  sourceSystem: string; // payroll-service | credit-service
  sourceReference: string;
  connector: string;
  status: string;
  currency: Currency;
  totalAmount: number;
  itemCount: number;
  /** Why a pending_verification batch is held (over a payout cap, or the dual-control gate). */
  holdReason?: string;
  submittedAt?: string;
  completedAt?: string;
  createdAt: string;
}

export interface PayoutDestination {
  type: string; // BANK | MOBILE_MONEY
  network: string;
  accountNumber: string; // masked (last 4)
  accountName: string;
}

export interface PayoutItem {
  id: string;
  employeeRef: string;
  amount: number;
  currency: Currency;
  status: string;
  connector: string;
  attempts: number;
  lastError?: string;
  /** Provider reference, MASKED by paygate (first 4 characters + …). */
  providerRef?: string;
  /** The most recent status check with the provider — why an item is still pending. */
  lastCheck?: ProviderCheck;
  submittedAt?: string;
  settledAt?: string;
  destination?: PayoutDestination;
}

/** One status check with the payment provider (read-only, no secrets). */
export interface ProviderCheck {
  checkedAt: string;
  resultCode?: string;
  /** Our mapped outcome: paid | failed | submitted (still pending). */
  status: string;
  /** The provider's status word / description. */
  detail?: string;
}

export interface PayoutBatchDetail extends PayoutBatch {
  items: PayoutItem[];
}

export interface PayoutSettings {
  /** Require an authorized user to verify & release each payout batch before dispatch. */
  requirePayoutVerification: boolean;
  /** Payout sanity caps (whole currency units). null/undefined = that cap is off. A batch that
   *  breaches any cap is held (pending_verification) with a reason, instead of dispatching. */
  minItemAmount?: number | null;
  maxItemAmount?: number | null;
  maxBatchTotal?: number | null;
  dailyCountCap?: number | null;
  dailyAmountCap?: number | null;
  /** Currency the caps are denominated in ('' = apply to any currency). */
  capsCurrency?: string;
}

const numOrNull = (v: any): number | null => (v === null || v === undefined ? null : Number(v));

function mapBatch(b: any): PayoutBatch {
  return {
    id: b.id,
    sourceSystem: b.source_system ?? '',
    sourceReference: b.source_reference ?? '',
    connector: b.connector ?? '',
    status: b.status ?? '',
    currency: (b.currency ?? 'TZS') as Currency,
    totalAmount: Number(b.total_amount ?? 0),
    itemCount: Number(b.item_count ?? 0),
    holdReason: b.hold_reason || undefined,
    submittedAt: b.submitted_at || undefined,
    completedAt: b.completed_at || undefined,
    createdAt: b.created_at ?? '',
  };
}

function mapItem(i: any): PayoutItem {
  const d = i.destination;
  return {
    id: i.id,
    employeeRef: i.employee_ref,
    amount: Number(i.amount ?? 0),
    currency: (i.currency ?? 'TZS') as Currency,
    status: i.status ?? '',
    connector: i.connector ?? '',
    attempts: Number(i.attempts ?? 0),
    lastError: i.last_error || undefined,
    providerRef: i.provider_ref || undefined,
    lastCheck: i.last_provider_check
      ? {
          checkedAt: i.last_provider_check.checked_at ?? '',
          resultCode: i.last_provider_check.result_code || undefined,
          status: i.last_provider_check.status ?? '',
          detail: i.last_provider_check.detail || undefined,
        }
      : undefined,
    submittedAt: i.submitted_at || undefined,
    settledAt: i.settled_at || undefined,
    destination: d
      ? { type: d.type ?? '', network: d.network ?? '', accountNumber: d.account_number ?? '', accountName: d.account_name ?? '' }
      : undefined,
  };
}

export const paygateApi = {
  async listPayouts(limit = 50): Promise<PayoutBatch[]> {
    const d = (await http.get<{ payouts: any[] }>(`${BASE}/payouts?limit=${limit}`)).data;
    return (d.payouts ?? []).map(mapBatch);
  },
  async getPayout(id: string): Promise<PayoutBatchDetail> {
    const b = (await http.get<any>(`${BASE}/payouts/${id}`)).data;
    return { ...mapBatch(b), items: (b.items ?? []).map(mapItem) };
  },
  /** SFTP host-to-host transmission trail for a batch (upload → bank ack/return). Empty for other rails. */
  async getTransmissions(id: string): Promise<TransmissionEntry[]> {
    const d = (await http.get<{ transmissions: TransmissionEntry[] }>(`${BASE}/payouts/${id}/transmissions`)).data;
    return d.transmissions ?? [];
  },
  async reconciliationQueue(): Promise<PayoutItem[]> {
    const d = (await http.get<{ items: any[] }>(`${BASE}/reconciliation`)).data;
    return (d.items ?? []).map(mapItem);
  },
  async resolveItem(itemId: string, resolution: 'paid' | 'failed', note: string): Promise<void> {
    await http.post(`${BASE}/payout-items/${itemId}/resolve`, { resolution, note });
  },
  /** Release a held (pending_verification) batch for dispatch — the dual-control gate. */
  async verifyPayout(batchId: string): Promise<void> {
    await http.post(`${BASE}/payouts/${batchId}/verify`, {});
  },
  /** Per-tenant payout controls (verification gate). */
  async getPayoutSettings(): Promise<PayoutSettings> {
    const d = (await http.get<any>(`${BASE}/settings`)).data;
    return {
      requirePayoutVerification: !!d.require_payout_verification,
      minItemAmount: numOrNull(d.min_item_amount),
      maxItemAmount: numOrNull(d.max_item_amount),
      maxBatchTotal: numOrNull(d.max_batch_total),
      dailyCountCap: numOrNull(d.daily_count_cap),
      dailyAmountCap: numOrNull(d.daily_amount_cap),
      capsCurrency: d.caps_currency ?? '',
    };
  },
  async savePayoutSettings(input: PayoutSettings): Promise<void> {
    // Send each cap explicitly (null clears it) so the form is the source of truth.
    await http.put(`${BASE}/settings`, {
      require_payout_verification: input.requirePayoutVerification,
      min_item_amount: input.minItemAmount ?? null,
      max_item_amount: input.maxItemAmount ?? null,
      max_batch_total: input.maxBatchTotal ?? null,
      daily_count_cap: input.dailyCountCap ?? null,
      daily_amount_cap: input.dailyAmountCap ?? null,
      caps_currency: input.capsCurrency ?? null,
    });
  },
  async listProviders(): Promise<ProviderConfig[]> {
    const d = (await http.get<{ providers: any[] }>(`${BASE}/providers`)).data;
    return (d.providers ?? []).map(mapConfig);
  },
  /** Every payment connection the caller may see, with what they may do. */
  async listConnections(): Promise<ConnectionsView> {
    const d = (await http.get<any>(`${BASE}/providers`)).data;
    return {
      connections: (d.providers ?? []).map(mapConfig),
      canManage: !!d.can_manage,
      canChooseLive: !!d.can_choose_live,
      liveAvailable: !!d.live_available,
    };
  },
  /** Non-financial connection test (M-Pesa: getSession only — never a payment). */
  async testConnection(id: string): Promise<ConnectionTestResult> {
    const d = (await http.post<any>(`${BASE}/providers/connections/${id}/test`, {})).data;
    return { ok: !!d.ok, detail: d.detail ?? '' };
  },
  /** Stop a connection from paying FUTURE payments; in-flight ones keep their binding. */
  async disableConnection(id: string): Promise<void> {
    await http.post(`${BASE}/providers/connections/${id}/disable`, {});
  },
  async saveProvider(code: ProviderCode, input: SaveProviderInput): Promise<void> {
    await http.put(`${BASE}/providers/${code}`, {
      ...(input.legalEntityId ? { legal_entity_id: input.legalEntityId } : {}),
      channel: input.channel ?? '',
      enabled: input.enabled ?? true,
      environment: input.environment ?? 'sandbox',
      priority: input.priority ?? 100,
      config: input.config ?? {},
      // Only send credentials when the admin actually entered them.
      ...(input.credentials != null ? { credentials: input.credentials } : {}),
    });
  },
  async deleteProvider(code: ProviderCode): Promise<void> {
    await http.delete(`${BASE}/providers/${code}`);
  },
};
